import Calculator from './Calculator.js';

/**
 * GroundCalculator - Calculator for ground/area calculations (m² × depth)
 *
 * Formula for ground (grunt):
 * - area = surface area in m²
 * - depth = depth in cm
 * - drainageRatio = drainage percentage (default 15%)
 *
 * Total volume:
 * - V = area_m2 × (depth_cm / 100) × 1000
 *
 * With drainage:
 * - Drainage volume = V × (drainageRatio / 100)
 * - Substrate volume = V - drainageVolume
 *
 * Without drainage:
 * - Substrate volume = V
 * - Drainage volume = 0
 *
 * @class GroundCalculator
 * @extends Calculator
 */
class GroundCalculator extends Calculator {
    /**
     * @param {Object} dimensions
     * @param {number} dimensions.area - Surface area in m²
     * @param {number} dimensions.depth - Depth in cm
     * @param {number} [dimensions.drainageRatio=15] - Drainage ratio percentage
     * @param {boolean} [withDrainage=true] - Whether to include drainage
     */
    constructor(dimensions, withDrainage = true) {
        super(dimensions);
        const { area = 0, depth = 0, drainageRatio = 15 } = dimensions;

        this.area = parseFloat(area) || 0;
        this.depth = parseFloat(depth) || 0;
        this.withDrainage = withDrainage;
        this.drainageRatio = drainageRatio;
    }

    /**
     * Calculate total volume in liters
     *
     * @returns {number} Total volume in liters
     */
    calculateTotalVolume() {
        const depthInMeters = this.depth / 100;
        return this.area * depthInMeters * 1000;
    }

    /**
     * Calculate substrate volume for ground
     *
     * @returns {number} Substrate volume in liters
     */
    calculateSubstrateVolume() {
        const totalVolume = this.calculateTotalVolume();
        const drainageVolume = this.calculateDrainageVolume();
        return totalVolume - drainageVolume;
    }

    /**
     * Calculate drainage volume for ground
     *
     * @returns {number} Drainage volume in liters
     */
    calculateDrainageVolume() {
        if (!this.withDrainage) {
            return 0;
        }

        const totalVolume = this.calculateTotalVolume();
        return totalVolume * (this.drainageRatio / 100);
    }

    /**
     * Set dimensions
     *
     * @param {Object} dimensions
     */
    setDimensions(dimensions) {
        Object.assign(this.dimensions, dimensions);
        if (dimensions.area !== undefined) this.area = parseFloat(dimensions.area) || 0;
        if (dimensions.depth !== undefined) this.depth = parseFloat(dimensions.depth) || 0;
        if (dimensions.withDrainage !== undefined) this.withDrainage = dimensions.withDrainage;
        if (dimensions.drainageRatio !== undefined) this.drainageRatio = parseFloat(dimensions.drainageRatio) || 15;
    }

    /**
     * Validate dimensions
     *
     * @returns {Object} Validation result
     */
    validate() {
        const errors = [];

        if (this.area <= 0) {
            errors.push({ field: 'area', type: 'required' });
        }
        if (this.depth <= 0) {
            errors.push({ field: 'depth', type: 'required' });
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }
}

export default GroundCalculator;